// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Spinner from '@mui/material/CircularProgress';
import Text from '@mui/material/Typography';
import VStack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import Thead from '@mui/material/TableHead';
import Tbody from '@mui/material/TableBody';
import Tr from '@mui/material/TableRow';
import Th from '@mui/material/TableCell';
import Td from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import AlertIcon from '@mui/material/AlertTitle';
import Modal from '@mui/material/Dialog';
import ModalHeader from '@mui/material/DialogTitle';
import ModalBody from '@mui/material/DialogContent';
import ModalFooter from '@mui/material/DialogActions';
import Input from '@mui/material/TextField';
import InputLeftElement from '@mui/material/InputAdornment';
import Icon from '@mui/material/IconButton';
import Textarea from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { remoteGet, remotePost } from '../utils/remoteApi';
import { useTheme } from '../context/ThemeContext';

// VPN Client Configs Table
const VpnClientConfigsTable = ({ ip, apiKey }) => {
  const { isDarkMode } = useTheme();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const fetchConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await remoteGet(ip, apiKey, '/api/vpn/client-configs');
      setConfigs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Failed to load configs');
      setSnackbarMessage('Failed to load configs');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchConfigs();
  }, [ip, apiKey]);

  const handleDownload = async (filename) => {
    try {
      const res = await remoteGet(ip, apiKey, `/api/vpn/client-config/${filename}`);
      const content = typeof res === 'string' ? res : (res?.data ?? JSON.stringify(res, null, 2));
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setSnackbarMessage(`Downloaded ${filename}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e) {
      setSnackbarMessage('Failed to download config.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) return;
    try {
      await remotePost(ip, apiKey, '/api/vpn/client-config/delete', {
        filename,
      });
      setSnackbarMessage(`Deleted ${filename}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchConfigs();
    } catch (e) {
      setSnackbarMessage('Failed to delete config.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <Spinner size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <AlertIcon>Error</AlertIcon>
        {error}
      </Alert>
    );
  }

  if (!configs.length) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <AlertIcon>Info</AlertIcon>
        No VPN client configs uploaded yet. Upload a .conf file above to get started.
      </Alert>
    );
  }

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          overflow: 'hidden',
          borderColor: 'divider',
        }}
      >
        <Table size="small">
          <Thead>
            <Tr>
              <Th
                sx={{
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.08)' : '#f5f7fa',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 1.5,
                }}
              >
                Filename
              </Th>
              <Th
                sx={{
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.08)' : '#f5f7fa',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 1.5,
                  textAlign: 'right',
                  width: 120,
                }}
              >
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {configs.map((cfg, idx) => (
              <Tr
                key={cfg.filename || cfg}
                sx={{
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                  },
                  borderBottom: idx < configs.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Td sx={{ py: 1.5, color: 'text.primary' }}>{cfg.filename || cfg}</Td>
                <Td sx={{ py: 1.5, textAlign: 'right' }}>
                  <Tooltip title="Download config file">
                    <IconButton
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={() => handleDownload(cfg.filename || cfg)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete config file">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(cfg.filename || cfg)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

// Styled drag-and-drop upload area for VPN Client Config
const StyledVpnUploadArea = ({ ip, apiKey, onUpload }) => {
  const { isDarkMode } = useTheme();
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [dragActive, setDragActive] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const data = {
        filenames: [file.name],
        contents: [text],
      };
      await remotePost(ip, apiKey, '/api/vpn/client-config', data);
      setSnackbarMessage('Uploaded client config');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      if (onUpload) onUpload();
    } catch (e) {
      setSnackbarMessage('Failed to upload config.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.conf')) {
        const fakeEvent = { target: { files: [file] } };
        handleUpload(fakeEvent);
      } else {
        setSnackbarMessage('Please select a .conf file.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };
  const handleClick = () => {
    inputRef.current.click();
  };
  const handleCloseSnackbar = () => setSnackbarOpen(false);

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      sx={{
        border: '2px dashed',
        borderColor: dragActive ? 'primary.main' : 'divider',
        borderRadius: 2,
        background: dragActive
          ? 'rgba(33, 150, 243, 0.08)'
          : isDarkMode
            ? 'rgba(138, 148, 168, 0.06)'
            : '#f5f7fa',
        p: 3,
        textAlign: 'center',
        cursor: uploading ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s',
        outline: dragActive ? '2px solid #2196f3' : 'none',
        mb: 2,
      }}
      onClick={handleClick}
    >
      <input
        type="file"
        accept=".conf"
        style={{ display: 'none' }}
        ref={inputRef}
        onChange={handleUpload}
        disabled={uploading}
      />
      <CloudUploadIcon
        sx={{
          fontSize: 48,
          color: dragActive ? 'primary.main' : 'grey.500',
          mb: 1,
        }}
      />
      <Text sx={{ color: 'text.primary', fontWeight: 500, fontSize: 16 }}>
        Drag & drop your <b>.conf</b> VPN client config here,
        <br />
        or click to select from your computer.
      </Text>
      <Text
        sx={{
          color: isDarkMode ? 'grey.400' : 'grey.600',
          fontSize: 13,
          mt: 1,
        }}
      >
        Only .conf files are accepted.
      </Text>
      {uploading && <Spinner size={24} sx={{ mt: 2 }} />}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

// Remove VPN Client Cache Button
const RemoveVpnClientCacheButton = ({ ip, apiKey }) => {
  const [isOpen, setIsOpen] = useState(false);
  const cancelRef = useRef();
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleRemove = async () => {
    setLoading(true);
    console.log(`[VPN Cache] Starting cache removal for IP: ${ip}`);
    try {
      const response = await remotePost(ip, apiKey, '/api/vpn/remove-client-cache', {});
      console.log('[VPN Cache] Cache removal successful:', response);
      setSnackbarMessage('VPN client cache removed successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      console.error('[VPN Cache] Failed to remove VPN client cache:', {
        error,
        response: error.response?.data,
        status: error.response?.status,
        ip,
        timestamp: new Date().toISOString(),
      });
      setSnackbarMessage(`Failed to remove VPN client cache: ${errorMessage}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  return (
    <>
      <Button
        variant="contained"
        color="error"
        onClick={() => setIsOpen(true)}
        loading={loading}
        sx={{ mb: 2 }}
      >
        Remove VPN Client Cache
      </Button>
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <ModalHeader>
          Remove VPN Client Cache
          <IconButton onClick={() => setIsOpen(false)} sx={{ ml: 'auto' }}>
            <CloseIcon />
          </IconButton>
        </ModalHeader>
        <ModalBody>
          Are you sure you want to remove the VPN client cache? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button variant="outlined" onClick={() => setIsOpen(false)} sx={{ mr: 3 }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleRemove} loading={loading}>
            Remove
          </Button>
        </ModalFooter>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

const StargateVpnClientWidgets = ({ ip, apiKey }) => {
  const { isDarkMode } = useTheme();
  const [refresh, setRefresh] = useState(0);
  const handleUploaded = () => setRefresh((r) => r + 1);

  return (
    <Card
      elevation={0}
      sx={{
        background: isDarkMode ? 'rgba(138, 148, 168, 0.04)' : '#ffffff',
        borderRadius: 3,
        boxShadow: 'none',
        border: '1.5px solid',
        borderColor: 'divider',
        maxWidth: 600,
        width: '100%',
        mx: 'auto',
        mt: 8,
        p: 0,
      }}
    >
      <CardHeader
        title={
          <Box>
            <Text variant="h6" sx={{ color: 'text.primary', fontWeight: 700, fontSize: 20 }}>
              VPN Client Configs
            </Text>
            <Text
              variant="body2"
              sx={{
                color: isDarkMode ? 'grey.400' : 'grey.600',
                mt: 0.5,
                fontWeight: 400,
              }}
            >
              Upload WireGuard configuration files (.conf) to use on client devices
            </Text>
          </Box>
        }
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
          mb: 0,
          py: 2,
        }}
      />
      <CardContent sx={{ pb: 2 }}>
        <StyledVpnUploadArea ip={ip} apiKey={apiKey} onUpload={handleUploaded} />
        <Box sx={{ mt: 2 }}>
          <VpnClientConfigsTable ip={ip} apiKey={apiKey} key={refresh} />
        </Box>
      </CardContent>
      <Divider sx={{ borderColor: 'divider', my: 0, mx: 0 }} />
      <CardHeader
        title={
          <Box>
            <Text variant="h6" sx={{ color: 'text.primary', fontWeight: 700, fontSize: 20 }}>
              VPN Client Cache
            </Text>
            <Text
              variant="body2"
              sx={{
                color: isDarkMode ? 'grey.400' : 'grey.600',
                mt: 0.5,
                fontWeight: 400,
              }}
            >
              Clear cached VPN client data to force reconnection with new configs
            </Text>
          </Box>
        }
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#ffffff',
          mb: 0,
          py: 2,
        }}
      />
      <CardContent sx={{ pt: 2, pb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Alert severity="info" sx={{ width: '100%' }}>
            <AlertIcon>Info</AlertIcon>
            Removing the cache will disconnect all VPN clients and require them to reconnect with
            updated configurations.
          </Alert>
          <RemoveVpnClientCacheButton ip={ip} apiKey={apiKey} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default StargateVpnClientWidgets;
